import { resolveLegacyGeniePath } from "@/data/brandMascots";
import { prettifyGuideTitle } from "@/pages/help-hub/guideTitle";
import { ApiService } from "@/services/api/ApiService";
import type {
  HelpHubDTO,
  HelpHubGuideDTO,
  HelpHubGuideSummary,
  HelpHubTopic,
} from "@/types/help-hub/help-hub";

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

/**
 * Fill in every field the page reads, whatever the server sent.
 *
 * Edge functions deploy separately from the app, so a browser running this
 * build routinely talks to the previous `api` — which has no `downloads`,
 * `questions` or per-guide `answers`. Defaulting here rather than at each of
 * the dozen read sites means one deploy-order skew can't white-screen a
 * customer's public page.
 */
const normaliseHub = (hub: HelpHubDTO): HelpHubDTO => ({
  ...hub,
  // A genie still carrying a retired bundled avatar path gets its replacement.
  branding: { ...hub.branding, logoUrl: resolveLegacyGeniePath(hub.branding?.logoUrl) ?? null },
  links: asArray(hub.links),
  extraPhones: asArray(hub.extraPhones),
  topics: asArray<HelpHubTopic>(hub.topics).map((topic) => ({
    ...topic,
    guides: asArray<HelpHubGuideSummary>(topic.guides).map((guide) => ({
      ...guide,
      // Uploaded sources carry their raw file name; a visitor gets a title.
      name: prettifyGuideTitle(guide.name ?? "") || guide.name,
      answers: asArray<string>(guide.answers),
    })),
  })),
  guideCount: typeof hub.guideCount === "number" ? hub.guideCount : 0,
  headline: hub.headline ?? null,
  subheadline: hub.subheadline ?? null,
  askPlaceholder: hub.askPlaceholder ?? null,
  questions: asArray(hub.questions),
  downloads: asArray(hub.downloads),
  contact: hub.contact ?? null,
  customSections: asArray(hub.customSections),
  // Default ON when the server predates the field — the band only ever offers
  // the app, so showing it against an older API is safe; hiding it isn't.
  showApp: hub.showApp !== false,
  searchEngineVisible: hub.searchEngineVisible === true,
});

/**
 * Public reads for the Support Hub. Both calls go through the `public` lane —
 * the hub is served to people who have never signed in and never will.
 *
 * `null` means not found or not published; the pages render a branded empty
 * state rather than surfacing an error.
 */
export class HelpHubService {
  static async getPublicHub(urlName: string): Promise<HelpHubDTO | null> {
    const response = await ApiService.invoke<{ hub: HelpHubDTO }>(
      {
        resource: "help-hub",
        action: "public-get",
        data: { url_name: urlName },
      },
      "public",
    );
    return response?.hub ? normaliseHub(response.hub) : null;
  }

  static async getPublicGuide(
    urlName: string,
    documentId: string,
  ): Promise<HelpHubGuideDTO | null> {
    const response = await ApiService.invoke<{ guide: HelpHubGuideDTO }>(
      {
        resource: "help-hub",
        action: "public-guide",
        data: { url_name: urlName, document_id: documentId },
      },
      "public",
    );
    if (!response?.guide) return null;
    return {
      ...response.guide,
      name: prettifyGuideTitle(response.guide.name ?? "") || response.guide.name,
    };
  }
}
